import { useState } from 'react'
import { AxiosResponse } from 'axios'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { Upload, Spin, message } from 'antd'
import { UploadRequestOption } from 'rc-upload/lib/interface'

import Box from '~/core/Box'
import Button from '~/core/Button'
import logger from '~/lib/logger'
import { uploadItem } from '~/lib/uploadFile'
import { isBlockedFileName } from '~/lib/fileExtensions'
import { UPLOAD_BLOCKED_ATTACHMENT_ERROR } from '~/lib/flashMsgs'

import styles from './styles.css'
import { UploadComponentType } from './types'

export default function UploadComponent(props: UploadComponentType) {
    const [loading, setLoading] = useState(false)

    const {
        onCreate,
        onSuccess,
        freezeIt,
        uploadCss,
        uploadTitle,
        multiple,
        listType,
        layoutType,
        isUploadDisabled,
    } = props

    const onUploadSuccess = (...args: AxiosResponse<{ id: string }>[]) => {
        const file = args[0].config.data

        setLoading(false)
        onSuccess(file)
    }

    const createEntity = (file: File) => {
        return onCreate(file)
    }

    const uploadEntity = (file: File) => {
        return uploadItem(file, {
            onSuccess: onUploadSuccess,
            onError: error => {
                logger.exception('File upload error', error)
                message.error('Failed to upload file.')
                setLoading(false)
            },
        })
    }

    const handleUpload = async (option: UploadRequestOption) => {
        const file = option.file as File

        if (isBlockedFileName(file.name)) {
            message.error(UPLOAD_BLOCKED_ATTACHMENT_ERROR)
            return
        }
        setLoading(true)
        const createdFile = await createEntity(file)

        if (!createdFile) {
            return null
        }
        return uploadEntity(createdFile)
    }

    return !layoutType ? (
        <Upload
            listType={listType}
            customRequest={handleUpload}
            className={uploadCss || styles.uploadImageCard}
            multiple={multiple}
            disabled={freezeIt}
        >
            {!loading ? (
                <Box direction="col">
                    <PlusOutlined style={{ fontSize: 22, marginBottom: 8 }} />
                    <Box direction="col" className="ant-upload-text">
                        {uploadTitle}
                    </Box>
                </Box>
            ) : (
                <Spin />
            )}
        </Upload>
    ) : (
        <Upload
            listType="text"
            customRequest={handleUpload}
            multiple={multiple}
            disabled={false}
            showUploadList={false}
        >
            <Button
                name="upload-attachment"
                placement="create-edit-exception-modal"
                data-test-target="upload-attachment-admin-note"
                disabled={isUploadDisabled || loading}
                className={styles.uploadButtonStyle}
                loading={loading}
            >
                <UploadOutlined />
                {uploadTitle || 'Upload attachment(s)'}
            </Button>
        </Upload>
    )
}
